import { createAsyncThunk } from '@reduxjs/toolkit';
import {
    callCreateReceipt,
    callDeleteReceipt,
    callDownloadImages,
    callGetReceipts,
    callGetServiceById,
    callGetServiceCategories,
    callUpdateReceipt
} from 'helpers/api_helper';
import {
    callGetReceiptPaymentReport,
    callGetServiceCategoryById,
} from './../../helpers/api_helper';

const toTotal = (response: any) => Number(response?.headers?.['x-total-count'] || 0);

export const getServiceCategories = createAsyncThunk(
  'services/callGetServiceCategories',
  async (param: any, { rejectWithValue }) => {
    try {
      const response: any = await callGetServiceCategories(param);
      return {
        data: response?.data,
        total: toTotal(response),
      };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getServiceCategoryById = createAsyncThunk(
  'services/callGetServiceCategoryById',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await callGetServiceCategoryById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getServiceById = createAsyncThunk(
  'services/callGetServiceById',
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await callGetServiceById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const getReceipts = createAsyncThunk(
  'services/callGetReceipts',
  async (param: any, { rejectWithValue }) => {
    try {
      const response: any = await callGetReceipts(param);
      return {
        data: response?.data,
        total: toTotal(response),
      };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

function getAllImagesFromObjects(arr: any[]) {
  return arr.flatMap((item: any) => {
    if (typeof item?.imagesJson === 'string') {
      try {
        const parsed = JSON.parse(item.imagesJson);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(item?.imagesJson) ? item.imagesJson : [];
  });
}

function replaceImagesJson(arr: any[], convertedImages: string[]) {
  let index = 0;

  return arr.map((item: any) => {
    const oldImages =
      typeof item?.imagesJson === 'string'
        ? (() => {
            try {
              const parsed = JSON.parse(item.imagesJson || '[]');
              return Array.isArray(parsed) ? parsed : [];
            } catch {
              return [];
            }
          })()
        : Array.isArray(item?.imagesJson)
          ? item.imagesJson
          : [];

    const newImages = convertedImages.slice(index, index + oldImages.length);
    index += oldImages.length;

    return {
      ...item,
      s3Path: oldImages,
      imagesJson: newImages,
    };
  });
}

export const getReceiptsWithPresign = createAsyncThunk(
  'services/callGetReceiptsWithPresign',
  async (param: any, { rejectWithValue }) => {
    try {
      const response: any = await callGetReceipts(param);
      const receipts: any[] = Array.isArray(response?.data) ? response.data : [];
      let data = receipts;
      if (!Array.isArray(receipts) || !receipts.length) {
        return {
          data,
          total: toTotal(response),
        };
      }

      const images = getAllImagesFromObjects(receipts);
      if (!images.length) {
        return {
          data,
          total: toTotal(response),
        };
      }

      const s3Images: any = await callDownloadImages({ storagePaths: images });
      if (!Array.isArray(s3Images) || !s3Images.length) {
        return {
          data,
          total: toTotal(response),
        };
      }

      const urls = s3Images.map((item: any) => item?.url).filter(Boolean);
      if (!urls.length) {
        return {
          data,
          total: toTotal(response),
        };
      }

      data = replaceImagesJson(receipts, urls);
      return {
        data,
        total: toTotal(response),
      };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const createReceipt = createAsyncThunk(
  'services/callCreateReceipt',
  async (param: any, { rejectWithValue }) => {
    try {
      const response = await callCreateReceipt(param);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const updateReceipt = createAsyncThunk(
  'services/callUpdateReceipt',
  async (payload: any, { rejectWithValue }) => {
    try {
      const { id, ...param } = payload;
      const response = await callUpdateReceipt(id, param);
      return response;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const deleteReceipt = createAsyncThunk(
  'services/callDeleteReceipt',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await callDeleteReceipt(id);
      return response;
    } catch (error) {
      return rejectWithValue('Unexpected error');
    }
  },
);

export const getReceiptPaymentReport = createAsyncThunk(
  'services/callGetReceiptPaymentReport',
  async (param: any, { rejectWithValue }) => {
    try {
      const response = await callGetReceiptPaymentReport(param);
      return response;
    } catch (error) {
      return rejectWithValue('Unexpected error');
    }
  },
);
